import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useEffect } from "react";
import { useSetRecoilState } from "recoil";

import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { isHandledByApiInterceptor } from "@/utilities/apiErrorReporting";
import { isRequestAbortedError } from "@/utilities/isRequestAbortedError";
import { isResponselessNetworkError } from "@/utilities/networkError";
import { NeedShowUpdateModalState } from "@/view_models/atoms";

// バージョン確認失敗時のログ出力。
// 端末のオフライン・電波断などレスポンスを伴わない通信失敗は、ベストエフォート処理の
// 想定内挙動（更新モーダルが出ないだけ）のため info に留める。
// CaptureConsole（src/index.tsx）は error / warn のみ拾うため、info であれば Sentry には送られない。
//
// API 呼び出し自体の失敗（504 などの応答エラー・タイムアウト含む）も info に留める。
// PropallyApiFactory の interceptor が status・パス・method 付きで Sentry へ送っており、
// ここで console.error すると同じ失敗が 2 つ目のイシューとして重複計上されるため
// （PROPALLY_WEB-269 と PROPALLY_WEB-280 がこれで分裂した）。サーバ側の異常検知は interceptor 側で維持される。
export const reportVersionCheckFailure = (err: unknown): void => {
  // 中断はログを出さず、通信エラーと API エラーは info、それ以外は error に分ける。
  if (isRequestAbortedError(err)) return;
  if (isResponselessNetworkError(err)) {
    console.info("バージョン確認をスキップしました（通信エラー）:", err);
    return;
  }
  if (isHandledByApiInterceptor(err)) {
    console.info("バージョン確認をスキップしました（API エラー。報告は interceptor 側）:", err);
    return;
  }
  console.error("バージョン確認失敗:", err);
};

// 文字列によるバージョン比較
export const compareVersions = (v1: string, v2: string): number => {
  const a = v1.split(".").map(Number);
  const b = v2.split(".").map(Number);
  const maxLength = Math.max(a.length, b.length);

  for (let i = 0; i < maxLength; i++) {
    const numA = a[i] || 0;
    const numB = b[i] || 0;
    if (numA > numB) return 1;
    if (numA < numB) return -1;
  }
  return 0;
};

export const AppVersionChecker = () => {
  const setNeedShowModal = useSetRecoilState(NeedShowUpdateModalState);
  useEffect(() => {
    const checkVersion = async () => {
      try {
        const minVersion = (await PropallyApiFactory().v1MinAppVersionGet()).data.min_version;

        if (Capacitor.getPlatform() !== "web") {
          const appVersion = (await App.getInfo()).version;
          if (compareVersions(appVersion, minVersion) < 0) {
            // バージョンが古い場合
            setNeedShowModal(true);
          }
        } else {
          setNeedShowModal(false);
        }
      } catch (err) {
        reportVersionCheckFailure(err);
      }
    };
    checkVersion();
  }, [setNeedShowModal]);
  return null;
};
